<template>
    <Modal id="sold-gift-voucher-data-modal"
           icon="fa-gift"
           size="lg"
           :custom-title-text="modalTitleText"
           :ok="onFormSubmit"
           :cancel="cancel">
        <form ref="sgvdm-form"
              class="sgvdm"
              @submit.prevent="onFormSubmit">
            <div class="sgvdm__left">
                <div v-if="giftVoucher.isBalanceType()"
                     class="sgvdm__balance form-section">
                    <h4>
                        <i class="fa-regular fa-money-bill"></i>
                        {{ l('GIFT_VOUCHERS.BALANCE_HEADING') }}
                    </h4>

                    <div v-if="giftVoucher.denominations.length < 1 && !giftVoucher.allowPriceEdit"
                         class="alert alert-danger">
                        {{ l('GIFT_VOUCHERS.NO_DENOMINATIONS') }}
                    </div>
                    <div v-else
                         class="selectable-btns btns">
                        <a v-for="denomination in giftVoucher.denominations"
                           :key="denomination.denomination"
                           class="btn btn-info btn-lg"
                           :class="[ (denomination.denomination === price ? 'active' : '') ]"
                           @click.prevent="selectDenomination(denomination)">
                            <div>
                                {{ currencyFilter(denomination.denomination) }}
                            </div>
                        </a>

                        <a v-if="giftVoucher.allowPriceEdit"
                           class="btn btn-info btn-lg"
                           :class="[ (customBalanceEntered ? 'active' : '') ]"
                           @click.prevent="enterCustomBalance()">
                            {{ l('GIFT_VOUCHERS.CUSTOM_BALANCE_BTN') }}
                            <span v-if="customBalanceEntered">({{ currencyFilter(price) }})</span>
                        </a>
                    </div>
                </div>

                <div class="sgvdm__delivery form-section">
                    <h4>
                        <i class="fa-solid fa-box"></i>
                        {{ l('GIFT_VOUCHERS.DELIVERY_TYPE_HEADING') }}
                    </h4>

                    <div class="selectable-btns btns">
                        <a v-for="deliveryType in giftVoucher.availableDeliveryTypes"
                           :key="deliveryType"
                           class="btn btn-info btn-lg"
                           :class="[ (data.deliveryType === deliveryType ? 'active' : '') ]"
                           @click.prevent="selectDeliveryType(deliveryType)">
                            <div>
                                {{ deliveryType }}
                            </div>
                        </a>
                    </div>
                </div>
            </div>
            <div v-if="showRightCol"
                 class="sgvdm__right">
                <div class="sgvdm__recipient form-section form-v form--on-light">
                    <h4>
                        <i class="fa-solid fa-user"></i>
                        {{ l('GIFT_VOUCHERS.RECIPIENT_INFO_HEADING') }}
                    </h4>

                    <div v-if="canCollectRecipientName"
                         class="form-group">
                        <label for="sold-gift-voucher-recipient-first-name"
                               class="control-label">
                            {{ l('CUSTOMER_DETAILS.NAME') }}
                            <small v-if="giftVoucher.mustCollectRecipientName()"
                                   class="required-label">{{ l('REQUIRED') }}</small>
                        </label>
                        <div class="row">
                            <div class="col-sm-6">
                                <input id="sold-gift-voucher-recipient-first-name"
                                       v-model="data.recipientContact.firstName"
                                       type="text"
                                       class="form-control input-lg input-first-name"
                                       :placeholder="firstNamePlaceholder"
                                       autocomplete="no-auto-fill" />
                            </div>
                            <div class="col-sm-6">
                                <input id="sold-gift-voucher-recipient-last-name"
                                       v-model="data.recipientContact.lastName"
                                       type="text"
                                       class="form-control input-lg input-last-name"
                                       :placeholder="lastNamePlaceholder"
                                       autocomplete="no-auto-fill" />
                            </div>
                        </div>
                    </div>

                    <div v-if="emailDeliveryTypeSelected"
                         class="form-group">
                        <label for="sold-gift-voucher-recipient-email"
                               class="control-label">{{ l('CUSTOMER_DETAILS.EMAIL') }}
                            <small v-if="emailDeliveryTypeSelected"
                                   class="required-label">{{ l('REQUIRED') }}</small>
                        </label>
                        <input id="sold-gift-voucher-recipient-email"
                               v-model="data.recipientContact.email"
                               type="email"
                               class="form-control input-lg"
                               :placeholder="emailPlaceholder"
                               autocomplete="no-auto-fill"
                               required />
                    </div>

                    <div v-if="canCollectRecipientMessage"
                         class="form-group">
                        <label for="sold-gift-voucher-message"
                               class="control-label">
                            {{ l('GIFT_VOUCHERS.MESSAGE_LABEL') }}
                            <small v-if="giftVoucher.mustCollectRecipientMessage()"
                                   class="required-label">{{ l('REQUIRED') }}</small>
                        </label>
                        <textarea id="sold-gift-voucher-message"
                                  v-model="data.recipientMessage"
                                  class="form-control input-lg"
                                  :placeholder="messagePlaceholder"
                                  autocomplete="no-auto-fill"
                                  :required="giftVoucher.mustCollectRecipientMessage()"></textarea>
                    </div>
                </div>
            </div>
        </form>
    </Modal>
</template>

<script>
import GiftVoucherDeliveryTypeEnum from '@/lib/GiftVouchers/GiftVoucherDeliveryTypeEnum';
import Modal from '@/vue/components/Modals/Modal';
import { CloseModalOnEscapeMixin } from '@/vue/mixins/CloseModalOnEscapeMixin';
import { container } from '@/container';
import { ModalPropsMixin } from '@/vue/mixins/ModalPropsMixin';
import { suppressModalCancelledErrorOrReThrow } from '@/lib/Errors';

export default {
    components: { Modal },

    mixins: [
        CloseModalOnEscapeMixin,
        ModalPropsMixin,
    ],

    props: {
        /**
         * One of 'add-new' or 'edit-new'.
         */
        action: {
            type: String,
            required: true,
        },

        /**
         * The GiftVoucher item being sold.
         */
        giftVoucher: {
            type: Object,
            required: true,
        },

        /**
         * The price this voucher will be sold for.
         */
        initialPrice: {
            type: [Number, String],
            default: null,
        },

        /**
         * The existing data for the voucher.
         * An instance of SoldGiftVoucherData.
         */
        data: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            price: this.initialPrice !== null ? parseFloat('' + this.initialPrice) : null,
        };
    },

    computed: {
        customBalanceEntered() {
            if (this.giftVoucher.isBalanceType() && this.price) {
                return this.giftVoucher.getDenominationsByValue(this.price).length < 1;
            }

            return false;
        },

        emailDeliveryTypeSelected() {
            return this.data.deliveryType === GiftVoucherDeliveryTypeEnum.EMAIL;
        },

        modalTitleText() {
            return container.langService.getText('GIFT_VOUCHERS.DATA_MODAL_TITLE') + ' ' + this.giftVoucher.getName();
        },

        showRightCol() {
            return this.canCollectRecipientMessage
                || this.canCollectRecipientName
                || (this.data.deliveryType && this.data.deliveryType !== 'In Person');
        },

        firstNamePlaceholder() {
            return container.langService.getText('CUSTOMER_DETAILS.FIRST_NAME');
        },

        lastNamePlaceholder() {
            return container.langService.getText('CUSTOMER_DETAILS.LAST_NAME');
        },

        emailPlaceholder() {
            return container.langService.getText('CUSTOMER_DETAILS.EMAIL');
        },

        messagePlaceholder() {
            return container.langService.getText('GIFT_VOUCHERS.MESSAGE_PLACEHOLDER');
        },

        canCollectRecipientName() {
            return this.giftVoucher.canCollectRecipientName();
        },

        canCollectRecipientMessage() {
            return this.giftVoucher.canCollectRecipientMessage();
        },
    },

    created() {
        if (this.action === 'add-new') {
            let autoSelectedBalance = false;
            if (this.giftVoucher.isBalanceType()
                && this.giftVoucher.denominations.length === 1
                && !this.giftVoucher.allowPriceEdit) {
                // If there is only one balance choice for a gift voucher select that by default.
                this.selectDenomination(this.giftVoucher.denominations[0]);
                autoSelectedBalance = true;
            }

            let autoSelectedDeliveryType = false;
            if (this.giftVoucher.availableDeliveryTypes.length === 1) {
                // If there is only one delivery type choice for a gift voucher select that by default.
                this.selectDeliveryType(this.giftVoucher.availableDeliveryTypes[0]);
                autoSelectedDeliveryType = true;
            }

            // If all of this applies there's nothing that can be done in this modal so just continue automatically.
            // Note we're inside an "if action = add-new" so this only runs when adding the voucher to the cart
            // not if the user clicks the edit button.
            if (
                // A balance was selected automatically, or it's not a balance voucher.
                (autoSelectedBalance || !this.giftVoucher.isBalanceType())
                // A delivery type was selected automatically.
                && autoSelectedDeliveryType
                // The delivery type is not email, meaning an email address needs to be collected.
                && !this.emailDeliveryTypeSelected
                // It's not possible add a message for the recipient.
                && !this.canCollectRecipientMessage
                // It's not possible to add a recipient name.
                && !this.canCollectRecipientName
            ) {
                console.debug('Auto submitting Gift Voucher modal.');
                setTimeout(() => {
                    this.onFormSubmit();
                }, 10);
            }
        }
    },

    methods: {
        onFormSubmit() {
            const validationResult = container.giftVoucherValidator.validate(
                this.giftVoucher,
                this.data,
                this.price,
            );

            if (validationResult !== true) {
                container.toastFactory.warningTop(validationResult);
                return;
            }

            this.resolve({
                price: this.price,
                soldGiftVoucherData: this.data,
            });
        },

        enterCustomBalance() {
            container.dialogService
                .numPad(
                    'GIFT_VOUCHERS.CUSTOM_BALANCE_DIALOG_MESSAGE',
                    {
                        initialValue: this.price,
                        min: 0.01,
                        title: 'GIFT_VOUCHERS.CUSTOM_BALANCE_DIALOG_TITLE',
                        numPadOptions: {
                            showCashButtons: true,
                            showSignButton: false,
                            showValueAsCurrency: true,
                        },
                    }
                )
                .then(({ value }) => {
                    if (value !== null) {
                        this.price = value;
                    }
                })
                .catch(suppressModalCancelledErrorOrReThrow);
        },

        /**
         * @param {GiftVoucherDenomination} denomination
         */
        selectDenomination(denomination) {
            this.price = denomination.denomination;
        },

        /**
         * @param {string} deliveryType
         */
        selectDeliveryType(deliveryType) {
            this.data.deliveryType = deliveryType;
        },
    },
};
</script>

<style lang="less">
@import (reference) "../../../../less/variables";

// Cool abbreviation 😎
.sgvdm {
    display: flex;
    grid-gap: @gutter-width;

    .form-group {
        padding: 10px;
    }
}

.sgvdm__left,
.sgvdm__right {
    flex-basis: 1%;
    flex-grow: 1;
    flex-shrink: 1;
}

.sgvdm__balance {
    // .sgvdm__delivery is always shown below this, so we can always have a margin.
    margin-bottom: @gutter-width;
}
</style>
